<script setup lang="ts">
import { computed, PropType } from "vue";

defineOptions({
  name: "UiIconButton",
});

type Sizes = "l" | "m" | "s";
type ColorType = "fill" | "ghost";

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },

  round: {
    type: Boolean,
    default: false,
  },

  size: {
    type: String as PropType<Sizes>,
    default: "m",
  },

  color: {
    type: String as PropType<ColorType>,
    default: "ghost",
  },

  icon: {
    type: String,
    default: "",
  },

  iconSize: {
    type: String,
    default: "",
  },

  iconColor: {
    type: String,
    default: "var(--color-semantic-content-normal-primary)",
  },

  tag: {
    type: String,
    default: "button",
  },
});

const component = computed(() => {
  switch (props.tag) {
    case "div":
      return "div";
    case "RouterLink":
      return "RouterLink";
    case "button":
      return "button";
    case "a":
      return "a";

    default:
      return "div";
  }
});
</script>

<template>
  <component
    :is="component"
    class="ui-icon-button"
    :class="{
      [`ui-icon-button__size_${props.size}`]: !!props.size,
      [`ui-icon-button__color_${props.color}`]: !!props.color,
      'ui-icon-button__custom-icon-size': !!props.iconSize,
      round: props.round,
    }"
    type="button"
    :disabled="props.disabled"
  >
    <slot>
      <span class="local-icon" :class="{ [icon]: props.icon }" />
    </slot>
  </component>
</template>

<style lang="scss" scoped>
.ui-icon-button {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  @include transition;
  border: 1px solid transparent;

  &.round {
    border-radius: 50%;
  }

  :deep(.warning-icon),
  :deep(.local-icon),
  :deep(.icon) {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    background-color: v-bind(iconColor);
    transition: 0.2s;
  }
  :deep(.warning-icon) {
    background-color: var(--color-semantic-content-hover-negative);
  }

  &__size {
    &_l {
      width: 40px;
      height: 40px;
      padding: 8px;
      flex-shrink: 0;

      :deep(.local-icon),
      :deep(.icon) {
        width: 24px;
        height: 24px;
      }
    }

    &_m {
      width: 32px;
      height: 32px;
      padding: 6px;
      flex-shrink: 0;

      :deep(.local-icon),
      :deep(.icon) {
        width: 20px;
        height: 20px;
      }
    }

    &_s {
      width: 24px;
      height: 24px;
      padding: 4px;
      flex-shrink: 0;

      :deep(.local-icon),
      :deep(.icon) {
        width: 16px;
        height: 16px;
      }
    }
  }

  &__custom-icon-size:deep(.icon) {
    width: v-bind("iconSize");
    height: v-bind("iconSize");
  }

  &__color {
    &_ghost {
      &:hover {
        background: var(--color-semantic-background-hover-primary);
      }

      &:active {
        background: var(--color-semantic-background-active-primary);
      }
    }

    &_fill {
      border: 1px solid var(--color-semantic-border-normal-soft);
      background: var(--color-semantic-background-hover-primary);

      &:hover:not(:disabled) {
        border-color: var(--color-semantic-border-hover-soft);
      }

      &:active(:not(:disabled)) {
        border-color: var(--color-semantic-border-hover-soft);
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
    background: transparent;

    .local-icon,
    :deep(.icon) {
      cursor: not-allowed;
      background: var(--color-semantic-content-other-disable);
    }
  }
}
</style>
